import { Sparkles } from 'lucide-react'
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { InlineEditor } from './solutionPrimitives'
import { Eyebrow, EyebrowAi, EyebrowAdd, IdentityRow } from './valueDesignPrimitives'
import { ConnectorLane } from './ConnectorLane'
import { PainRow, OutcomeRow, RelieverRow, CreatorRow } from './editorialRows'
import { SortableSection, renderFillerSlots, renderLeftEmpties } from './valueDesignSlots'
import type { ReorderKind } from './valueDesignSlots'
import { getRelieverPainIds, getCreatorGainIds } from './PainGainRows'
import {
  painLabel, reliefLabel, outcomeLabel, creatorLabel,
  type SpreadLayout,
} from './valueDesignLayout'
import { painRelieverStarters, gainCreatorStarters } from './vpcSuggestions'
import type {
  VPCPainReliever, VPCGainCreator, JtbdData, PersonaData,
} from '@ispot/shared'

interface Pain    { id: string; description: string; severity?: string }
interface Outcome { id: string; description: string; importance?: string }

interface Props {
  persona: PersonaData | null
  jtbd:    JtbdData    | null
  pains: Pain[]
  outcomes: Outcome[]
  relievers: VPCPainReliever[]
  creators:  VPCGainCreator[]
  productName: string
  valueProposition: string
  layout: SpreadLayout
  byPain:    number[][]
  byReliever:number[][]
  byOutcome: number[][]
  byCreator: number[][]
  hoveredId: string | null
  linkedSet: Set<string>
  onHover:   (id: string | null) => void
  onChangeValueProp:  (v: string) => void
  onAiDraft:          (block: string) => void
  onAddReliever:      (painId: string) => void
  onUpdateReliever:   (idx: number, description: string) => void
  onRemoveReliever:   (idx: number) => void
  onToggleReliever:   (relieverIdx: number, painId: string) => void
  onAddCreator:       (gainId: string) => void
  onUpdateCreator:    (idx: number, description: string) => void
  onRemoveCreator:    (idx: number) => void
  onToggleCreator:    (creatorIdx: number, gainId: string) => void
  onReorder: (kind: ReorderKind, activeId: string, overId: string) => void
  aiLoading: string | null
  apiKeyAvailable: boolean
}

export function ValueDesignSpread(props: Props) {
  const {
    persona, pains, outcomes, relievers, creators,
    productName, valueProposition,
    layout, byPain, byReliever, byOutcome, byCreator,
    hoveredId, linkedSet, onHover,
    onChangeValueProp, onAiDraft,
    onAddReliever, onUpdateReliever, onRemoveReliever, onToggleReliever,
    onAddCreator, onUpdateCreator, onRemoveCreator, onToggleCreator,
    onReorder, aiLoading, apiKeyAvailable,
  } = props

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const aiTip = apiKeyAvailable ? undefined : 'Set API key in Settings first'

  const personaName     = persona?.name || 'Unnamed persona'
  const personaInitials = personaName.split(/\s+/).map(w => w[0] || '').join('') || 'U'
  const personaSubtitle = [
    persona?.role,
    persona?.decisionPower && `Decision: ${persona.decisionPower}`,
  ].filter(Boolean).join(' · ') || 'Role not set'
  const archetypeNode = persona?.archetype
    ? <span className="text-[11.5px] italic text-text-muted">“{persona.archetype}”</span>
    : undefined

  // LinkChip option builders (one per row direction)
  const relieverOptionsForPain   = (painId: string)         => relievers.map((r, j) => ({ idLabel: reliefLabel(j),   text: r.description || '', checked: getRelieverPainIds(r).includes(painId) }))
  const painOptionsForReliever   = (r: VPCPainReliever)     => pains.map((p, i)     => ({ idLabel: painLabel(i),     text: p.description,       checked: getRelieverPainIds(r).includes(p.id) }))
  const creatorOptionsForOutcome = (gainId: string)         => creators.map((c, j) => ({ idLabel: creatorLabel(j),   text: c.description || '', checked: getCreatorGainIds(c).includes(gainId) }))
  const outcomeOptionsForCreator = (c: VPCGainCreator)      => outcomes.map((o, i) => ({ idLabel: outcomeLabel(i),   text: o.description,       checked: getCreatorGainIds(c).includes(o.id) }))

  const painTargets    = pains.map((p, i)    => ({ idLabel: painLabel(i),    text: p.description }))
  const outcomeTargets = outcomes.map((o, i) => ({ idLabel: outcomeLabel(i), text: o.description }))

  const openPainStarters = painRelieverStarters(pains).filter(s => {
    const i = pains.findIndex(p => p.id === s.painId); return i >= 0 && byPain[i].length === 0
  })
  const openGainStarters = gainCreatorStarters(outcomes).filter(s => {
    const i = outcomes.findIndex(g => g.id === s.gainId); return i >= 0 && byOutcome[i].length === 0
  })

  return (
    <div
      className="grid relative px-12 bg-bg flex-1"
      style={{
        gridTemplateColumns: 'minmax(0,1fr) 96px minmax(0,1fr)',
        gridTemplateRows: layout.gridTemplateRows,
      }}
    >
      {/* Identity — customer */}
      <div style={{ gridColumn: 1, gridRow: 2 }} className="min-w-0 pr-4">
        <IdentityRow
          side="customer" primary={personaName} secondary={personaSubtitle}
          initials={personaInitials} photo={persona?.photo} trailing={archetypeNode}
        />
      </div>

      {/* Identity — solution (with inline value-prop editor) */}
      <div style={{ gridColumn: 3, gridRow: 2 }} className="min-w-0 pl-4 flex flex-col justify-center gap-1">
        <span className="text-[14.5px] font-semibold leading-tight text-text truncate">{productName}</span>
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-1 min-w-0 italic text-text-secondary text-[12.5px]">
            <InlineEditor
              value={valueProposition} onChange={onChangeValueProp}
              placeholder="Click to write your value proposition…"
              multiline italic size="sm"
            />
          </div>
          <button
            onClick={() => onAiDraft('valueProposition')}
            disabled={!!aiLoading || !apiKeyAvailable}
            title={aiTip}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11.5px] text-text-muted hover:bg-warm-100 hover:text-accent-500 flex-shrink-0 disabled:opacity-40"
          >
            <Sparkles className="w-2.5 h-2.5" />
            {aiLoading === 'valueProposition' ? '…' : 'Refine'}
          </button>
        </div>
      </div>

      {/* Eyebrows — pains side & relievers side */}
      <div style={{ gridColumn: 1, gridRow: 4 }} className="pr-4">
        <Eyebrow label="Key pains" count={pains.length} />
      </div>
      <div style={{ gridColumn: 3, gridRow: 4 }} className="pl-4">
        <Eyebrow label="Pain relievers" count={relievers.length}>
          <EyebrowAdd
            label="Add" popoverHeading="Add reliever for…"
            options={painTargets}
            onSelect={(idx) => onAddReliever(pains[idx].id)}
            disabled={pains.length === 0}
          />
          {pains.length > 0 && (
            <EyebrowAi
              onClick={() => onAiDraft('painRelievers')}
              loading={aiLoading === 'painRelievers'}
              disabled={!!aiLoading || !apiKeyAvailable}
              title={aiTip}
            />
          )}
        </Eyebrow>
      </div>

      {/* Pain rows (sortable) */}
      <SortableSection kind="pain" items={pains.map(p => p.id)} sensors={sensors} onReorder={onReorder}>
        {pains.map((pain, i) => {
          const id = painLabel(i)
          return (
            <PainRow
              key={pain.id} gridRow={layout.painStartRow + i} id={id} sortableId={pain.id}
              pain={pain}
              linkIds={byPain[i]?.map(j => reliefLabel(j)) ?? []}
              options={relieverOptionsForPain(pain.id)}
              onToggleLink={(relieverIdx) => onToggleReliever(relieverIdx, pain.id)}
              hovered={hoveredId === id} linked={linkedSet.has(id)} onHover={onHover}
            />
          )
        })}
      </SortableSection>
      {renderLeftEmpties({
        itemCount: pains.length, totalSlots: layout.maxPainRows,
        startGridRow: layout.painStartRow, label: painLabel, keyPrefix: 'pain',
      })}

      {/* Reliever rows (sortable; starters + empties trail below) */}
      <SortableSection kind="reliever" items={relievers.map((r, i) => r.id ?? `r-${i}`)} sensors={sensors} onReorder={onReorder}>
        {relievers.map((r, slotIdx) => {
          const id = reliefLabel(slotIdx)
          const sId = r.id ?? `r-${slotIdx}`
          return (
            <RelieverRow
              key={sId} gridRow={layout.painStartRow + slotIdx} id={id} sortableId={sId}
              reliever={r} index={slotIdx}
              linkIds={byReliever[slotIdx]?.map(i => painLabel(i)) ?? []}
              options={painOptionsForReliever(r)}
              onUpdate={onUpdateReliever} onRemove={onRemoveReliever}
              onToggleLink={(painIdx) => onToggleReliever(slotIdx, pains[painIdx].id)}
              hovered={hoveredId === id} linked={linkedSet.has(id)} onHover={onHover}
            />
          )
        })}
      </SortableSection>
      {renderFillerSlots({
        responseCount: relievers.length, totalSlots: layout.maxPainRows,
        startGridRow: layout.painStartRow, responseLabel: reliefLabel,
        starters: openPainStarters.map(s => {
          const targetIdx = pains.findIndex(p => p.id === s.painId)
          return {
            targetIdLabel: painLabel(targetIdx), targetText: s.text,
            verb: 'Add reliever', onClick: () => onAddReliever(s.painId),
          }
        }),
        keyPrefix: 'reliever',
      })}

      {/* Eyebrows — outcomes side & creators side */}
      <div style={{ gridColumn: 1, gridRow: layout.outcomeStartRow - 1 }} className="pr-4">
        <Eyebrow label="Desired outcomes" count={outcomes.length} />
      </div>
      <div style={{ gridColumn: 3, gridRow: layout.outcomeStartRow - 1 }} className="pl-4">
        <Eyebrow label="Gain creators" count={creators.length}>
          <EyebrowAdd
            label="Add" popoverHeading="Add creator for…"
            options={outcomeTargets}
            onSelect={(idx) => onAddCreator(outcomes[idx].id)}
            disabled={outcomes.length === 0}
          />
          {outcomes.length > 0 && (
            <EyebrowAi
              onClick={() => onAiDraft('gainCreators')}
              loading={aiLoading === 'gainCreators'}
              disabled={!!aiLoading || !apiKeyAvailable}
              title={aiTip}
            />
          )}
        </Eyebrow>
      </div>

      {/* Outcome rows (sortable) */}
      <SortableSection kind="outcome" items={outcomes.map(o => o.id)} sensors={sensors} onReorder={onReorder}>
        {outcomes.map((outcome, i) => {
          const id = outcomeLabel(i)
          return (
            <OutcomeRow
              key={outcome.id} gridRow={layout.outcomeStartRow + i} id={id} sortableId={outcome.id}
              outcome={outcome}
              linkIds={byOutcome[i]?.map(j => creatorLabel(j)) ?? []}
              options={creatorOptionsForOutcome(outcome.id)}
              onToggleLink={(creatorIdx) => onToggleCreator(creatorIdx, outcome.id)}
              hovered={hoveredId === id} linked={linkedSet.has(id)} onHover={onHover}
            />
          )
        })}
      </SortableSection>
      {renderLeftEmpties({
        itemCount: outcomes.length, totalSlots: layout.maxOutcomeRows,
        startGridRow: layout.outcomeStartRow, label: outcomeLabel, keyPrefix: 'out',
      })}

      {/* Creator rows (sortable) */}
      <SortableSection kind="creator" items={creators.map((c, i) => c.id ?? `c-${i}`)} sensors={sensors} onReorder={onReorder}>
        {creators.map((c, slotIdx) => {
          const id = creatorLabel(slotIdx)
          const sId = c.id ?? `c-${slotIdx}`
          return (
            <CreatorRow
              key={sId} gridRow={layout.outcomeStartRow + slotIdx} id={id} sortableId={sId}
              creator={c} index={slotIdx}
              linkIds={byCreator[slotIdx]?.map(i => outcomeLabel(i)) ?? []}
              options={outcomeOptionsForCreator(c)}
              onUpdate={onUpdateCreator} onRemove={onRemoveCreator}
              onToggleLink={(outcomeIdx) => onToggleCreator(slotIdx, outcomes[outcomeIdx].id)}
              hovered={hoveredId === id} linked={linkedSet.has(id)} onHover={onHover}
            />
          )
        })}
      </SortableSection>
      {renderFillerSlots({
        responseCount: creators.length, totalSlots: layout.maxOutcomeRows,
        startGridRow: layout.outcomeStartRow, responseLabel: creatorLabel,
        starters: openGainStarters.map(s => {
          const targetIdx = outcomes.findIndex(g => g.id === s.gainId)
          return {
            targetIdLabel: outcomeLabel(targetIdx), targetText: s.text,
            verb: 'Add creator', onClick: () => onAddCreator(s.gainId),
          }
        }),
        keyPrefix: 'creator',
      })}

      {/* Connector lane */}
      <ConnectorLane
        layout={layout}
        byReliever={byReliever} byCreator={byCreator}
        painCount={pains.length} relieverCount={relievers.length}
        outcomeCount={outcomes.length} creatorCount={creators.length}
        hoveredId={hoveredId} linkedSet={linkedSet}
      />
    </div>
  )
}
